import React, { useState, useRef, useEffect } from "react";
import { cloudApi } from "../../api";
import axios from "axios";
import Sidebar from "../Sidebar/Sidebar";
import "./CreatePharmacyBranch.css";

const CreatePharmacyBranch = () => {
  const [formData, setFormData] = useState({
    branch_name: "",
    email: "",
    phone_number: "",
    user_name: "",
    password: "",           // ✅ was hashed_password — renamed to match backend schema
    branch_address: "",
    opening_hours: "",
    supervising_pharmacist_name: "",
    supervising_pharmacist_license: "",
    latitude: "",
    longitude: "",
    state: "",
    city: "",
    country: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalMessage, setGeneralMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const inputRefs = useRef({});

  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

  // ✅ Automatically fetch geolocation when address fields change
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (
        formData.branch_address.trim() &&
        formData.city.trim() &&
        formData.country.trim()
      ) {
        const fullAddress = `${formData.branch_address}, ${formData.city}, ${formData.country}`;
        try {
          const geo = await axios.get(
            "https://maps.googleapis.com/maps/api/geocode/json",
            { params: { address: fullAddress, key: GOOGLE_API_KEY } }
          );

          const results = geo?.data?.results;
          if (results && results.length > 0) {
            const loc = results[0].geometry.location;
            setFormData((prev) => ({
              ...prev,
              latitude: loc.lat.toFixed(6),
              longitude: loc.lng.toFixed(6),
            }));
          }
        } catch (err) {
          console.error("❌ Geocoding failed:", err);
        }
      }
    }, 800);

    return () => clearTimeout(timeout);
  }, [formData.branch_address, formData.city, formData.country, GOOGLE_API_KEY]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setGeneralMessage("");
    setErrorMessage("");
  };

  const scrollToFirstError = () => {
    const firstErrorField = Object.keys(fieldErrors).find(
      (key) => fieldErrors[key]
    );
    if (firstErrorField && inputRefs.current[firstErrorField]) {
      inputRefs.current[firstErrorField].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      inputRefs.current[firstErrorField].focus();
    }
  };

  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0) {
      scrollToFirstError();
    }
  }, [fieldErrors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralMessage("");
    setErrorMessage("");
    setFieldErrors({});

    const payload = {
      ...formData,
      latitude: parseFloat(formData.latitude) || 0,
      longitude: parseFloat(formData.longitude) || 0,
    };

    try {
      await cloudApi.post("/users/pharmacy-branch/create", payload);

      setGeneralMessage("✅ Pharmacy branch created successfully!");
      setFormData({
        branch_name: "",
        email: "",
        phone_number: "",
        user_name: "",
        password: "",         // ✅ was hashed_password
        branch_address: "",
        opening_hours: "",
        supervising_pharmacist_name: "",
        supervising_pharmacist_license: "",
        latitude: "",
        longitude: "",
        state: "",
        city: "",
        country: "",
      });
      setFieldErrors({});
    } catch (error) {
      console.error("❌ Error creating pharmacy branch:", error);
      const detail = error.response?.data?.detail;

      if (Array.isArray(detail)) {
        const newFieldErrors = {};
        detail.forEach((err) => {
          const field = err.loc && err.loc.length > 1 ? err.loc[1] : null;
          if (field) {
            let normalizedField = field;
            if (field === "phone") normalizedField = "phone_number";
            newFieldErrors[normalizedField] = err.msg;
          }
        });
        setFieldErrors(newFieldErrors);
      } else if (typeof detail === "string") {
        const lowerDetail = detail.toLowerCase();
        if (lowerDetail.includes("email")) {
          setFieldErrors({ email: detail });
        } else if (lowerDetail.includes("phone")) {
          setFieldErrors({ phone_number: detail });
        } else if (
          lowerDetail.includes("username") ||
          lowerDetail.includes("user_name")
        ) {
          setFieldErrors({ user_name: detail });
        } else if (lowerDetail.includes("password")) {
          setFieldErrors({ password: detail });
        } else {
          setErrorMessage(detail);
        }
      } else {
        setErrorMessage("❌ Failed to create pharmacy branch");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <Sidebar />
      <main className="main-content">
        <h1 className="title">🏪 Create Pharmacy Branch</h1>

        <form onSubmit={handleSubmit} className="branch-form" noValidate>
          {Object.keys(formData)
            .filter((key) => !["latitude", "longitude"].includes(key))
            .map((key) => (
              <div key={key} className="form-group">
                <label htmlFor={key}>{key.replace(/_/g, " ")}</label>
                <input
                  id={key}
                  ref={(el) => (inputRefs.current[key] = el)}
                  type={key.includes("password") ? "password" : "text"}
                  name={key}
                  value={formData[key]}
                  onChange={handleChange}
                  required
                  aria-describedby={`${key}-error`}
                  aria-invalid={!!fieldErrors[key]}
                />
                {fieldErrors[key] && (
                  <div id={`${key}-error`} className="error-text">
                    {fieldErrors[key]}
                  </div>
                )}
              </div>
            ))}

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? "Creating..." : "Create Branch"}
            </button>
          </div>

          {generalMessage && (
            <div className="success-message-inline">{generalMessage}</div>
          )}
          {errorMessage && (
            <div className="error-message-bottom">{errorMessage}</div>
          )}
        </form>
      </main>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default CreatePharmacyBranch;